import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import {Alert, Flex, message, Table, Tabs} from "antd";
import BlockLoading from "../../../../components/LoadingComponents/BlockLoading";
import {LeftOutlined} from "@ant-design/icons";
import MyText from "../../../../components/UI/Text/MyText";
import {LocalName} from "../../../../utils/LocalName";
import GroupService from "../../../../services/GroupService";
import {createStyles} from "antd-style";

const useStyle = createStyles(({css, token}) => {
    const {antCls} = token;
    return {
        customTable: css`
            ${antCls}-table {
                ${antCls}-table-container {
                    ${antCls}-table-body,
                    ${antCls}-table-content {
                        scrollbar-width: thin;
                        scrollbar-color: #eaeaea transparent;
                        scrollbar-gutter: stable;
                    }
                }
            }
        `,
        // отчисление: серая строка занятия после даты отчисления
        afterLeaveRow: css`
            & > td {
                background: #f5f5f5 !important;
                color: #bfbfbf;
            }
        `,
    };
});
const StudentGroupPage = () => {
    const {id} = useParams()
    const {user} = useSelector(state => state.user);
    const navigate = useNavigate();

    const [journal, setJournal] = useState(null);
    const [isLoading, setLoading] = useState(false);

    const {styles} = useStyle();

    useEffect(() => {
        handleFetchData();
    }, []);

    useEffect(() => {

    }, []);

    async function handleFetchData() {
        setLoading(true);
        await GroupService.getGroupOfStudent(id, user.id)
            .then((r) => {
                setJournal(r.data);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                setLoading(false);
            })
    }

    const columns1 = [
        {
            title: <MyText center><IntlMessage id={"tg.nameOfLesson"}/></MyText>,
            width: '20%',
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => (
                <p>
                    {record.journalTopic.name}
                </p>
            ),
        },
        {
            title: <MyText center><IntlMessage id={"tg.dateOfLesson"}/></MyText>,
            width: '20%',
            dataIndex: 'date',
            key: 'date',
            render: (_, record) => (
                <p>
                    {record.date ? new Date(record.date).toLocaleDateString('kk-KZ') : "-"}
                </p>
            ),
        },
        {
            title: "",
            width: '30%',
            dataIndex: 'attendance',
            key: 'attendance',
            render: (_, record) => (
                // п.7: уроки до присоединения помечаются ☁️ и не учитываются в статистике
                // fix joinDate: ☁️ — только пометка «тебя ещё не было в группе». Если преподаватель всё же
                // отметила присутствие на таком уроке, урок учитывается в проценте — показываем обычную отметку.
                // fix joinDate: причина показывается только когда она реально стоит. Снятие причины
                // пишет isAbsentForReason = false (третье состояние, раньше были только true/null),
                // и проверка `!== null` уводила ячейку в пустой <span>{null}</span> навсегда.
                // отчисление: занятие после даты отчисления — серая строка, без ❌ (с ученика не требуется)
                record.afterLeave && !record.isPresent ? '—' :
                record.beforeJoin && !record.isPresent ? <span title="До присоединения">☁️</span> :
                    record.isAbsentForReason && record.reasonOfAbsent ?
                        <span>{record.reasonOfAbsent}</span> : record.isPresent ? '✅' : record.isAbsent ? '❌' : ""
            )
        },
    ];

    const testColumns = (journal?.test || []).map((test, index) => ({
        title: `${test.name} (${test.maxGrade})`,
        dataIndex: `test_${index}`,
        key: `test_${index}`,
        align: 'center',
        render: (value) => value !== undefined && value !== null ? value : '-',
    }));

    const projectColumns = (journal?.project || []).map((project, index) => ({
        title: `${project.name} (${project.maxGrade})`,
        dataIndex: `project_${index}`,
        key: `project_${index}`,
        align: 'center',
        render: (value) => value !== undefined && value !== null ? value : '-',
    }));

    const finalTestColumn = journal?.finalTest
        ? [{
            title: 'Final Test',
            dataIndex: 'finalTest',
            key: 'finalTest',
            align: 'center',
            render: (value) => value?.grade ?? '-',
        }]
        : [];

    const gradeColumn = {
        title: 'Grade',
        dataIndex: 'grade',
        key: 'grade',
        align: 'center',
        render: (value) => value?.grade ?? '-',
    };

// Собираем все колонки
    const columns2 = [
        ...projectColumns,
        ...testColumns,
        ...finalTestColumn,
        gradeColumn,
    ];

// Создаём строку данных
    const dataSource2 = [{
        key: '1',
        // Данные из project
        ...Object.fromEntries(
            (journal?.project || []).map((project, index) => [`project_${index}`, project.grade?.grade ?? null])
        ),
        // Данные из test
        ...Object.fromEntries(
            (journal?.test || []).map((test, index) => [`test_${index}`, test.grade?.grade ?? null])
        ),
        // Финальный тест и итоговая оценка
        finalTest: journal?.finalTest,
        grade: journal?.grade,
    }];

    const tabItems = [
        {
            key: '1',
            label: <IntlMessage id={'journal'}/>,
            children: <Table
                bordered
                className={styles.customTable}
                pagination={false}
                columns={columns1}
                dataSource={journal?.lessons}
                rowClassName={record => record?.afterLeave ? styles.afterLeaveRow : ''}
                scroll={{x: 'max-content'}}
                size="small"
            />,
        },
        {
            key: '2',
            label: <IntlMessage id={'tg.grades'}/>,
            children: <Table
                bordered
                className={styles.customTable}
                pagination={false}
                columns={columns2}
                dataSource={dataSource2}
                scroll={{x: 'max-content'}}
                size="small"
            />,
        }
    ]

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} vertical gap={20}>
            <BlockLoading isLoading={isLoading}/>
            {!isLoading &&
                <Flex gap={"middle"} vertical>
                    <Flex gap={"middle"}>
                        <LeftOutlined style={{cursor: "pointer"}} onClick={() => navigate("/p/groups")}/>
                        <MyText uppercase strong
                                size={"large"}>{LocalName.getName(journal) + " " + journal?.number} | {journal?.teacherFullName}/{journal?.teacherFullNameEng} | {journal?.academicYear}</MyText>
                    </Flex>
                    {/* п.7: процент посещаемости с даты присоединения */}
                    {journal?.attendancePercent !== null && journal?.attendancePercent !== undefined &&
                        <MyText strong>
                            <IntlMessage id={'tg.attendancePercent'}/>: {journal.attendancePercent}%
                            {journal?.joinDate && ` (${new Date(journal.joinDate).toLocaleDateString('kk-KZ')})`}
                        </MyText>
                    }
                    {/* отчисление: журнал и оценки до даты отчисления остаются доступны */}
                    {journal?.leaveDate &&
                        <Alert type="warning" showIcon
                               message={<IntlMessage id={'tg.youAreExpelled'} values={{
                                   date: String(journal.leaveDate).slice(0, 10).split('-').reverse().join('.')
                               }}/>}/>
                    }
                </Flex>
            }
            <Tabs style={{width: "100%"}} defaultActiveKey={"1"} type="card" items={tabItems}/>
        </Flex>
    );
};

export default StudentGroupPage;